import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { MdsTooltip, MdsTooltipPlacement } from "@/design-system/components/tooltip";
import { mdsColors } from "@/design-system/foundations";
import { css } from "@/domains/emotion";
import { EventContext } from "@/domains/metrics/context";
import {
  BottomButton,
  RightButton,
  LeftButton,
  SplitButtonWrapper,
  TopButton,
  SoloButton,
  SoloIconButton,
} from "@/mds/components/buttons/split-button/split-button.style";
import { useAppStore } from "@/store/utils/hooks";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";

interface SidebarCreateButtonsProps {
  isCollapsed: boolean;
  onClick?: () => void;
}

export const SidebarCreateButtons = observer<SidebarCreateButtonsProps>(
  ({ isCollapsed, onClick }) => {
    const { store } = useAppStore();
    const isDisabled = store.notes.newNoteIsBeingCreated;
    const isVoiceDisabled = isDisabled || store.audioCapture.isStartRecordingDisabled;
    const isAudioCaptureEnabled = store.spaceAccountFeatureFlags.getFlagStatus("audio-capture");

    const handleWriteClick = useCallback(() => {
      onClick?.();
      store.notes.composeNewNote({ eventContext: EventContext.GlobalActions });
    }, [onClick, store.notes]);

    const handleVoiceClick = useCallback(() => {
      onClick?.();
      store.notes.composeNewNote({
        eventContext: EventContext.GlobalActions,
        autoStartVoice: true,
      });
    }, [onClick, store.notes]);

    if (isCollapsed) {
      return (
        <CollapsedButtonWrapper>
          {!isAudioCaptureEnabled && (
            <MdsTooltip
              config={{
                content: "Write",
                placement: MdsTooltipPlacement.Right,
              }}
            >
              <SoloIconButton onPress={handleWriteClick} isDisabled={isDisabled}>
                <MdsIcon kind={MdsIconKind.ComposeAlt} className={iconClass} />
              </SoloIconButton>
            </MdsTooltip>
          )}
          {isAudioCaptureEnabled && (
            <>
              <MdsTooltip
                config={{
                  content: "Write",
                  placement: MdsTooltipPlacement.Right,
                }}
              >
                <TopButton onPress={handleWriteClick} isDisabled={isDisabled}>
                  <MdsIcon kind={MdsIconKind.ComposeAlt} className={iconClass} />
                </TopButton>
              </MdsTooltip>

              <MdsTooltip
                config={{
                  content: "Speak",
                  placement: MdsTooltipPlacement.Right,
                }}
              >
                <BottomButton onPress={handleVoiceClick} isDisabled={isVoiceDisabled}>
                  <MdsIcon kind={MdsIconKind.Microphone} className={iconClass} />
                </BottomButton>
              </MdsTooltip>
            </>
          )}
        </CollapsedButtonWrapper>
      );
    }

    return (
      <ButtonWrapper>
        <SplitButtonWrapper>
          {!isAudioCaptureEnabled && (
            <SoloButton onPress={handleWriteClick} isDisabled={isDisabled}>
              <MdsIcon kind={MdsIconKind.ComposeAlt} className={iconClass} />
              <PrimaryText>Write</PrimaryText>
            </SoloButton>
          )}

          {isAudioCaptureEnabled && (
            <>
              <LeftButton onPress={handleWriteClick} isDisabled={isDisabled}>
                <MdsIcon kind={MdsIconKind.ComposeAlt} className={iconClass} />
                <PrimaryText>Write</PrimaryText>
              </LeftButton>
              <RightButton onPress={handleVoiceClick} isDisabled={isVoiceDisabled}>
                <MdsIcon kind={MdsIconKind.Microphone} className={iconClass} />
                <PrimaryText>Speak</PrimaryText>
              </RightButton>
            </>
          )}
        </SplitButtonWrapper>
      </ButtonWrapper>
    );
  }
);

// This 1px pad gives the button's border room to show
const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1px;
  flex: 1;
  margin: 0 ${({ theme }) => theme.spacing.xs};
`;

// This 1px pad gives the button's border room to show
const CollapsedButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1px;
  flex: 1;
  margin: 0 ${({ theme }) => theme.spacing.sm};
`;

const iconClass = css({ color: mdsColors().grey.x600 });

const PrimaryText = styled.span`
  color: ${({ theme }) => theme.colors.grey.x600};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;
